/**
 * Raw flow read/update hooks (Standard tab v2) — per tracker.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Voluum: GET/PUT /voluum/flows/:id/raw, key ['voluum','flow','raw',id].
 * Tres Commas: /tres-commas/flows/:id/raw, key ['tres-commas','flow','raw',id].
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TrackerId, VoluumFlowResource, VoluumFlowUpdate } from '@trafficpulse/shared';

import {
  rawFlowApiOf,
  rawFlowKey,
} from '@/features/flow-optimizer/services/raw-flow/raw-flow-api';
import {
  hydrateFlowPathAiSplit,
  mergeTrafficPulsePathAiFromDraft,
} from '@/features/flow-optimizer/tabs/standard/v2/utils/path-ai';

function apiOrThrow(provider: TrackerId) {
  const rawApi = rawFlowApiOf(provider);
  if (!rawApi) throw new Error(`No raw flow editor for ${provider}`);
  return rawApi;
}

export function useRawFlow(flowId: string | undefined, provider: TrackerId) {
  return useQuery({
    queryKey: rawFlowKey(provider, flowId ?? ''),
    queryFn: () => apiOrThrow(provider).get(flowId!),
    enabled: Boolean(flowId) && rawFlowApiOf(provider) !== null,
    staleTime: 30_000,
  });
}

export function useUpdateRawFlow(flowId: string, provider: TrackerId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: VoluumFlowUpdate) => apiOrThrow(provider).update(flowId, body),
    onSuccess: (data, draft) => {
      const merged = JSON.parse(JSON.stringify(data.flow)) as VoluumFlowResource;
      mergeTrafficPulsePathAiFromDraft(merged, draft);
      hydrateFlowPathAiSplit(merged);
      queryClient.setQueryData<VoluumFlowResource>(rawFlowKey(provider, flowId), merged);
      queryClient.invalidateQueries({ queryKey: [provider, 'flow'] });
    },
  });
}
